import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 ────────────────────────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

const Ico = {
  Upload: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

function UploadPage() {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  const [file,         setFile]         = useState(null);
  const [isUploading,  setIsUploading]  = useState(false);
  const [customAlert,  setCustomAlert]  = useState({ show: false, message: '' });

  const showAlert  = (msg) => setCustomAlert({ show: true, message: msg });
  const closeAlert = ()    => setCustomAlert({ show: false, message: '' });

  useEffect(() => {
    const userData = localStorage.getItem('currentUser');
    if (userData) { try { JSON.parse(userData); } catch (e) {} }
  }, []);

  const handleUpload = async () => {
    if (!file) return showAlert('파일을 선택해주세요!');

    const supportedFormats = ['flac', 'm4a', 'mp3', 'mp4', 'mpeg', 'mpga', 'oga', 'ogg', 'wav', 'webm'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!supportedFormats.includes(fileExt)) {
      return showAlert(`지원하지 않는 파일 형식입니다.\n지원 형식: ${supportedFormats.join(', ')}`);
    }

    setIsUploading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('currentUser'));
      const userId   = userData?.userId || '2';

      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', String(userId));
      formData.append('title', file.name);

      const response = await fetch('https://campus-life-ai.onrender.com/api/ai/whisper', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.transcript) {
        localStorage.setItem('summary', data.summary);
        localStorage.setItem('transcript', data.transcript);
        navigate('/summary');
      } else if (data.error) {
        showAlert(`분석 실패: ${data.error}`);
      } else {
        showAlert('음성 변환에 실패했습니다. 파일 형식을 확인해주세요.\n지원 형식: flac, m4a, mp3, mp4, wav, webm');
      }
    } catch {
      showAlert('AI 서버 연결 실패! 서버가 실행 중인지 확인해주세요.');
    } finally {
      setIsUploading(false);
    }
  };

  const supportedFormats = ['flac', 'm4a', 'mp3', 'mp4', 'wav', 'webm', 'ogg'];

  return (
    <div style={{ ...S.root, overflowX: 'hidden' }}>

      {/* ── 알림 모달 ── */}
      {customAlert.show && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, padding: isMobile ? '24px 20px' : '32px 40px' }}>
            <p style={S.modalMsg}>{customAlert.message}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button style={S.modalBtn} onClick={closeAlert}>확인</button>
            </div>
          </div>
        </div>
      )}

      <main style={{
        ...S.main,
        // 모바일: 위아래 패딩 줄이기
        padding: isMobile ? '24px 14px 60px' : '60px 20px',
        gap: isMobile ? '14px' : '20px',
      }}>

        {/* 페이지 타이틀 */}
        <div style={S.pageTitle}>
          <h2 style={{ ...S.pageTitleText, fontSize: isMobile ? '18px' : '24px' }}>
            🎙️ AI 강의 학습 시스템
          </h2>
          <p style={{ ...S.pageTitleSub, fontSize: isMobile ? '12px' : '13px' }}>
            강의 녹음 파일을 업로드하면 Whisper AI가 분석을 시작합니다.
          </p>
        </div>

        {/* ── 카드: 파일 업로드 ── */}
        <div style={{
          ...S.card,
          // 모바일: 카드 패딩 줄이기, 최대 너비 100%
          padding: isMobile ? '18px 16px' : '22px 24px',
          maxWidth: isMobile ? '100%' : '600px',
        }}>
          <CardHeader icon={Ico.Upload} title="강의 파일 업로드" />

          {/* 지원 형식 뱃지 */}
          <div style={S.formatRow}>
            {supportedFormats.map(fmt => (
              <span key={fmt} style={S.formatBadge}>{fmt}</span>
            ))}
          </div>

          {/* 드롭존 */}
          <div style={{
            ...S.dropzone,
            // 모바일: 패딩 줄이기
            padding: isMobile ? '24px 16px' : '36px 20px',
          }}>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              style={S.fileInput}
              accept=".flac,.m4a,.mp3,.mp4,.mpeg,.mpga,.oga,.ogg,.wav,.webm"
            />
            {file ? (
              <div style={S.fileSelectedInner}>
                <span style={{ fontSize: '22px' }}>✅</span>
                <span style={{ ...S.fileSelectedName, fontSize: isMobile ? '12px' : '13px', wordBreak: 'break-all' }}>
                  {file.name}
                </span>
              </div>
            ) : (
              <>
                <span style={{ fontSize: isMobile ? '24px' : '30px' }}>📂</span>
                <p style={{ ...S.dropzoneText, fontSize: isMobile ? '13px' : '14px' }}>
                  {isMobile ? '파일을 선택하세요' : '파일을 클릭하거나 드래그해서 업로드하세요'}
                </p>
                <p style={S.dropzoneSub}>flac, m4a, mp3, mp4, wav, webm 지원</p>
              </>
            )}
          </div>

          {/* 분석 버튼 */}
          <button
            style={{
              ...(isUploading ? S.disabledBtn : S.primaryBtn),
              fontSize: isMobile ? '14px' : '15px',
              padding: isMobile ? '12px' : '14px',
            }}
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? 'AI 분석 중...' : '분석 시작하기'}
          </button>
        </div>

      </main>
    </div>
  );
}

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh',
    backgroundColor: '#f4f7f9',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
  },
  main: {
    padding: '60px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  pageTitle:     { textAlign: 'center', marginBottom: '4px' },
  pageTitleText: { fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px' },
  pageTitleSub:  { fontSize: '13px', color: '#64748b', margin: 0 },
  card: {
    backgroundColor: '#fff',
    borderRadius: '14px',
    padding: '22px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9',
    width: '100%',
    maxWidth: '600px',
    boxSizing: 'border-box',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  cardIcon: {
    width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: BLUE_BG, color: BLUE,
  },
  cardTitle:    { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  formatRow:    { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '18px' },
  formatBadge:  { padding: '3px 9px', backgroundColor: BLUE_BG, color: BLUE, borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  dropzone: {
    border: '2px dashed #bfdbfe',
    borderRadius: '12px',
    padding: '36px 20px',
    marginBottom: '18px',
    backgroundColor: '#f8fafc',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  fileInput: {
    position: 'absolute', width: '100%', height: '100%',
    top: 0, left: 0, opacity: 0, cursor: 'pointer',
  },
  dropzoneText:      { fontSize: '14px', color: BLUE, fontWeight: '600', margin: 0, textAlign: 'center' },
  dropzoneSub:       { fontSize: '12px', color: '#94a3b8', margin: 0, textAlign: 'center' },
  fileSelectedInner: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' },
  fileSelectedName:  { fontSize: '13px', fontWeight: '600', color: '#1e40af' },
  primaryBtn: {
    width: '100%', padding: '14px',
    backgroundColor: BLUE, color: '#fff',
    border: 'none', borderRadius: '10px',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
    boxSizing: 'border-box',
  },
  disabledBtn: {
    width: '100%', padding: '14px',
    backgroundColor: '#adb5bd', color: '#fff',
    border: 'none', borderRadius: '10px',
    fontSize: '15px', fontWeight: '700', cursor: 'not-allowed',
    boxSizing: 'border-box',
  },
  modalOverlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: '#fff', padding: '32px 40px',
    borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
    textAlign: 'center', minWidth: '280px', boxSizing: 'border-box',
    width: '90%', maxWidth: '360px',
  },
  modalMsg: { fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: '0 0 24px', lineHeight: '1.5', whiteSpace: 'pre-line' },
  modalBtn: { padding: '9px 24px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default UploadPage;